import React, { useLayoutEffect, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MyTeam, SortConfig, SortField, PoolContribution } from './typesMyTeams';
import { useUserStore } from '../../lib/userStore';
import { ArrowUpDown, ArrowUp, ArrowDown, RefreshCw, Ban as Bandage, PlusCircle, Clock, Star, Shield, Eye, EyeOff } from 'lucide-react';
import { translate } from '../../lib/translations';
import { usePlayerCooldown } from '../../lib/hooks/usePlayerCooldown';
import { InjuryDescriptionModal } from '../Modals/InjuryDescriptionModal';
import { useEmbedStore } from '../../lib/embedStore';

interface MyTeamsTableProps {
  players: MyTeam[];
  sortConfig: SortConfig;
  onSort: (field: SortField) => void;
  onRelease: (player: MyTeam) => void;
  onToggleReserve: (player: MyTeam) => void;
  onSwapSelect: (player: MyTeam) => void;
  selectedPlayer: number | null;
  multiSelectMode?: boolean;
  isPlayerSelectedInMultiMode?: (player: MyTeam) => boolean;
  language: 'en' | 'fr';
  injuredPlayers: Array<{
    playerid: number; 
    description: string; 
    returned_date?: string;
    days_remaining?: number;
    injury_unique_id?: string;
    returned_injury_unique_id?: string;
  }>;
  activeSection?: 'MAIN' | 'RESERVES' | 'INJURED' | 'SCRATCH';
  onSendToInjuredList?: (player: MyTeam) => void;
  onReactivateFromInjured?: (player: MyTeam) => void;
  onMoveToScratch?: (player: MyTeam) => void;
  onRemoveFromScratch?: (player: MyTeam) => void;
  onToggleTradingBloc?: (player: MyTeam, status: boolean) => void;
  onToggleIntouchable?: (player: MyTeam, isIntouchable: boolean) => void;
  onViewDetails?: (player: MyTeam) => void;
  hideActions?: boolean;
  // Watchlist props
  watchlistPlayerIds?: Set<number>;
  onAddToWatchlist?: (playerId: number) => void;
  onRemoveFromWatchlist?: (playerId: number) => void;
  watchlistActionInProgress?: boolean;
  partnerLimitedActions?: boolean;
  // Pool stats
  statsMode?: 'nhl' | 'pool';
  poolStats?: Map<number, PoolContribution>;
}

interface Column {
  field: SortField;
  translationKey: string;
  sortable?: boolean;
  getValue?: (player: MyTeam) => number | string;
}

const SELECTION_PERCENTAGE_COLUMN_KEY = 'selection.percentage';

const columns: Column[] = [
  { field: 'skaterfullname', translationKey: 'name', sortable: true },
  { field: 'gamesplayed', translationKey: 'gp', sortable: true },
  { field: 'goals', translationKey: 'goals', sortable: true },
  { field: 'assists', translationKey: 'assists', sortable: true },
  { field: 'points', translationKey: 'points', sortable: true },
  {
    field: 'points',
    translationKey: 'ppg',
    sortable: true,
    getValue: (player) =>
      player.gamesplayed > 0
        ? Number((player.points / player.gamesplayed).toFixed(2))
        : 0,
  },  
  { field: 'positioncode', translationKey: 'position' },
  { field: 'teamabbrevs', translationKey: 'team' },
  { field: 'salary', translationKey: 'salary', sortable: true },

  { field: 'pppoints', translationKey: 'ppp', sortable: true },
  { field: 'shpoints', translationKey: 'shp', sortable: true },
  { field: 'gamewinninggoals', translationKey: 'gwg', sortable: true },
];

export function MyTeamsTable({
  players,
  sortConfig,
  onSort,
  onRelease,
  onToggleReserve,
  onSwapSelect,
  selectedPlayer,
  multiSelectMode = false,
  isPlayerSelectedInMultiMode,
  language,
  injuredPlayers,
  activeSection = 'MAIN',
  onSendToInjuredList,
  onReactivateFromInjured,
  onMoveToScratch,
  onRemoveFromScratch,
  onToggleTradingBloc,
  onToggleIntouchable,
  onViewDetails,
  hideActions = false,
  watchlistPlayerIds = new Set(),
  onAddToWatchlist = () => {},
  onRemoveFromWatchlist = () => {},
  watchlistActionInProgress = false,
  partnerLimitedActions = false,
  statsMode = 'nhl',
  poolStats = new Map()
}: MyTeamsTableProps) {
  const { activeCooldowns, startCooldown, getRemainingTime } = usePlayerCooldown();
  const isBpmSession = useUserStore((s) => s.is_bpm_session);
  const isTslhSession = useUserStore((s) => s.is_tslh_session);
  const isEmbed = useEmbedStore((s) => s.isEmbed);
  const limitedActions = isBpmSession || partnerLimitedActions || (isEmbed && !!isTslhSession);
  const [tooltipInfo, setTooltipInfo] = useState<{ x: number, y: number, playerId: number, action: string } | null>(null);
  const [imageErrors, setImageErrors] = useState<{[key: number]: boolean}>({});
  const [showInjuryModal, setShowInjuryModal] = useState(false);
  const [selectedInjury, setSelectedInjury] = useState<{ playerId: number, playerName: string, description: string, daysRemaining?: number, isReturned?: boolean } | null>(null);
  const [openActionsForId, setOpenActionsForId] = useState<number | null>(null);
  const actionsToggleRef = useRef<HTMLElement | null>(null);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);
  const [actionsMenuStyle, setActionsMenuStyle] = useState<React.CSSProperties>({});

  const visibleColumns = (statsMode === 'pool'
    ? columns.filter(c => !['plusminus', 'pppoints', 'shpoints', 'gamewinninggoals', 'penaltyminutes'].includes(c.field))
    : columns).reduce<Column[]>((visible, column, index) => {
      visible.push(column);
      if (index === 0 && players.some((player) => player.selection_percentage !== undefined)) {
        visible.push({
          field: 'selection_percentage',
          translationKey: SELECTION_PERCENTAGE_COLUMN_KEY,
          sortable: true,
        });
      }
      return visible;
    }, []);

  const renderSortIcon = (field: SortField, sortable?: boolean) => {
    if (!sortable) return null;

    if (sortConfig.field !== field) {
      return <ArrowUpDown size={16} className="ms-1 text-muted opacity-50" />;
    }

    return sortConfig.direction === 'asc' ? (
      <ArrowUp size={16} className="ms-1 text-primary" />
    ) : (
      <ArrowDown size={16} className="ms-1 text-primary" />
    );
  };

  const handleViewDetailsClick = (player: MyTeam, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onViewDetails?.(player);
  };

  const getCellValue = (player: MyTeam, column: Column) => {
    if (column.field === 'selection_percentage') {
      return `${Math.round(Number(player.selection_percentage ?? 0))} %`;
    }
    if (statsMode === 'pool') {
      const contrib = poolStats.get(player.playerid);
      const poolGamesPlayed = contrib?.pool_gamesplayed ?? 0;
      if (column.getValue) {
        // PPG en mode pool : pool_points / pool_gamesplayed
        const poolPts = contrib?.pool_points ?? 0;
        return poolGamesPlayed > 0 ? Number((poolPts / poolGamesPlayed).toFixed(2)) : 0;
      }
      if (column.field === 'gamesplayed') return poolGamesPlayed;
      if (column.field === 'goals') return contrib?.pool_goals ?? 0;
      if (column.field === 'assists') return contrib?.pool_assists ?? 0;
      if (column.field === 'points') return contrib?.pool_points ?? 0;
    }
    if (column.getValue) {
      return column.getValue(player);
    }
    if (column.field === 'salary') {
      const formatCurrency = (amount: number) => new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);

      // Check if player has salary retention received (other team paying part of salary)
      const hasRetention = (player as any).salary_retention_received_pct && (player as any).salary_retention_received_pct > 0;
      
      if (hasRetention) {
        const retentionPct = (player as any).salary_retention_received_pct;
        const retainedAmount = (player as any).salary_retention_received_amount || 0;
        const effectiveSalary = player.salary - retainedAmount;
        
        return (
          <div style={{ whiteSpace: 'nowrap' }}>
            <div className="text-success fw-bold">{formatCurrency(effectiveSalary)}</div>
            <small className="text-muted d-block" style={{ fontSize: '0.7rem', lineHeight: '1.2' }}>
              {formatCurrency(player.salary)} (-{retentionPct}%)
            </small>
          </div>
        );
      }
      
      return formatCurrency(player.salary);
    }
    return player[column.field];
  };

  const handleReleaseClick = (player: MyTeam, e: React.MouseEvent) => {
    if (activeCooldowns.has(player.playerid)) {
      e.preventDefault();
      return;
    }

    startCooldown(player.playerid);
    onRelease(player);
  };

  const handleToggleReserveClick = (player: MyTeam, e: React.MouseEvent) => {
    if (activeCooldowns.has(player.playerid)) {
      e.preventDefault();
      return;
    }

    startCooldown(player.playerid);
    onToggleReserve(player);
  };
  
  const handleSwapSelectClick = (player: MyTeam) => {
    if (!activeCooldowns.has(player.playerid)) {
      onSwapSelect(player);
    }
  };
  
  const handleSendToInjuredListClick = (player: MyTeam, e: React.MouseEvent) => {
    if (activeCooldowns.has(player.playerid)) {
      e.preventDefault();
      return;
    }

    startCooldown(player.playerid);
    if (onSendToInjuredList) {
      onSendToInjuredList(player);
    }
  };

  const handleReactivateFromInjuredClick = (player: MyTeam, e: React.MouseEvent) => {
    if (activeCooldowns.has(player.playerid)) {
      e.preventDefault();
      return;
    }

    startCooldown(player.playerid);
    if (onReactivateFromInjured) {
      onReactivateFromInjured(player);
    }
  };

  const handleMoveToScratchClick = (player: MyTeam, e: React.MouseEvent) => {
    if (activeCooldowns.has(player.playerid)) {
      e.preventDefault();
      return;
    }

    startCooldown(player.playerid);
    if (onMoveToScratch) {
      onMoveToScratch(player);
    }
  };

    const handleRemoveFromScratchClick = (player: MyTeam, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemoveFromScratch) {
      startCooldown(player.playerid);
      onRemoveFromScratch(player);
    }
  };

  const handleToggleTradingBlocClick = (player: MyTeam, status: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleTradingBloc) {
      startCooldown(player.playerid);
      onToggleTradingBloc(player, status);
    }
  };

  const handleToggleIntouchableClick = (player: MyTeam, isIntouchable: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleIntouchable) {
      startCooldown(player.playerid);
      onToggleIntouchable(player, isIntouchable);
    }
  };

  const handleImageError = (playerId: number) => {
    setImageErrors(prev => ({...prev, [playerId]: true}));
  };

  const getPlayerImageUrl = (player: MyTeam) => {
    if (imageErrors[player.playerid]) {
      return "https://assets.nhle.com/mugs/nhl/20242025/EDM/8478402.png";
    }
    return `https://assets.nhle.com/mugs/nhl/20242025/${player.teamabbrevs}/${player.playerid}.png`;
  };

  const handleMouseEnter = (e: React.MouseEvent, playerId: number, action: string) => {
    if (activeCooldowns.has(playerId)) {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipInfo({
        x: rect.left + window.scrollX,
        y: rect.top + window.scrollY - 25,
        playerId,
        action
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltipInfo(null);
  };

  const isPlayerInjured = (playerId: number) => {
    const injury = injuredPlayers.find(p => p.playerid === playerId);
    if (!injury) return false;
    
    // Player is injured if:
    // 1. No returned_date at all (never returned), OR
    // 2. Has returned_date but injury_unique_id doesn't match returned_injury_unique_id (different injury, current one is still active)
    return !injury.returned_date || 
           (injury.injury_unique_id && injury.injury_unique_id !== injury.returned_injury_unique_id);
  };

  const isPlayerReturnedFromInjury = (playerId: number) => {
    const player = injuredPlayers.find(p => p.playerid === playerId);
    // Player has returned only if returned_date exists AND it matches the current injury_unique_id
    return player && player.returned_date && player.injury_unique_id === player.returned_injury_unique_id ? true : false;
  };

  const getInjuryDescription = (playerId: number) => {
    const injury = injuredPlayers.find(p => p.playerid === playerId);
    return injury ? injury.description : '';
  };
  
  const getDaysRemaining = (playerId: number) => {
    const player = injuredPlayers.find(p => p.playerid === playerId);
    return player && player.days_remaining !== undefined ? player.days_remaining : 0;
  };

  const handleShowInjuryModal = (player: MyTeam) => {
    const isReturned = isPlayerReturnedFromInjury(player.playerid);
    const daysRemaining = getDaysRemaining(player.playerid);
    
    setSelectedInjury({
      playerId: player.playerid,
      playerName: player.skaterfullname,
      description: getInjuryDescription(player.playerid),
      daysRemaining: daysRemaining,
      isReturned: isReturned
    });
    setShowInjuryModal(true);
  };

  useEffect(() => {
    const handleMouseMove = () => {
      if (tooltipInfo) {
        setTooltipInfo(null);
      }
    };

    window.addEventListener('scroll', handleMouseMove);
    
    return () => {
      window.removeEventListener('scroll', handleMouseMove);
    };
  }, [tooltipInfo]);

  // Watchlist functions
  const handleAddToWatchlistClick = (player: MyTeam, e: React.MouseEvent) => {
    e.stopPropagation();
    onAddToWatchlist(player.playerid);
  };

  const handleRemoveFromWatchlistClick = (player: MyTeam, e: React.MouseEvent) => {
    e.stopPropagation();
    onRemoveFromWatchlist(player.playerid);
  };

  const isInWatchlist = (playerId: number) => {
    return watchlistPlayerIds.has(playerId);
  };

  const formatShortName = (fullName: string) => {
    const normalized = (fullName || '').trim().replace(/\s+/g, ' ');
    if (!normalized) return '';

    const parts = normalized.split(' ');
    if (parts.length < 2) return normalized;

    const firstInitial = parts[0]?.[0]?.toUpperCase() || '';
    const lastName = parts[parts.length - 1] || '';
    if (!firstInitial || !lastName) return normalized;
    return `${firstInitial}.${lastName}`;
  };

  const closeActionsMenu = () => {
    setOpenActionsForId(null);
    actionsToggleRef.current = null;
  };

  const updateActionsMenuPosition = () => {
    const toggleEl = actionsToggleRef.current;
    const menuEl = actionsMenuRef.current;
    if (!toggleEl || !menuEl) return;

    const rect = toggleEl.getBoundingClientRect();
    const padding = 8;

    const menuWidth = Math.max(220, menuEl.offsetWidth || 0);
    const naturalMenuHeight = menuEl.offsetHeight || 0;

    const spaceBelow = window.innerHeight - rect.bottom - padding;
    const spaceAbove = rect.top - padding;
    const openUp = spaceBelow < naturalMenuHeight && spaceAbove > spaceBelow;

    const available = Math.max(140, openUp ? spaceAbove : spaceBelow);
    const maxHeight = Math.min(available, window.innerHeight - padding * 2);
    const visibleHeight = Math.min(naturalMenuHeight || maxHeight, maxHeight);

    let top = openUp ? rect.top - visibleHeight : rect.bottom;
    if (top < padding) top = padding;
    if (top + visibleHeight > window.innerHeight - padding) {
      top = window.innerHeight - padding - visibleHeight;
    }

    let left = rect.left;
    if (left + menuWidth > window.innerWidth - padding) {
      left = window.innerWidth - padding - menuWidth;
    }
    if (left < padding) left = padding;

    setActionsMenuStyle({
      position: 'fixed',
      top,
      left,
      zIndex: 3000,
      maxHeight,
      overflowY: 'auto',
      minWidth: 220,
    });
  };

  useLayoutEffect(() => {
    if (openActionsForId === null) return;
    const raf = requestAnimationFrame(() => {
      updateActionsMenuPosition();
    });
    return () => cancelAnimationFrame(raf);
  }, [openActionsForId]);

  useEffect(() => {
    if (openActionsForId === null) return;

    const onDocPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;

      if (actionsMenuRef.current?.contains(target)) return;
      if (actionsToggleRef.current?.contains(target)) return;
      closeActionsMenu();
    };

    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeActionsMenu();
    };

    const onScrollOrResize = () => {
      updateActionsMenuPosition();
    };

    document.addEventListener('pointerdown', onDocPointerDown, true);
    document.addEventListener('keydown', onEscape);
    window.addEventListener('resize', onScrollOrResize);
    // scroll does not bubble; capture to catch scrolling containers
    document.addEventListener('scroll', onScrollOrResize, true);

    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onEscape);
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [openActionsForId]);

  const sectionClass = `myteams-section-${activeSection.toLowerCase()}`;

  return (
    <div className={`table-responsive ${sectionClass}`}>
      <table className="table table-hover table-sm align-middle myteams-table myteams-players">
        <thead>
          <tr>
            {!hideActions && (
              <th className="myteams-th-actions" aria-label="Actions" title="Actions"></th>
            )}
            <th>Photo</th>
            {visibleColumns.map((column) => (
              <th
                key={`${column.field}-${column.translationKey}`}
                onClick={() => column.sortable && onSort(column.field)}
                style={{ cursor: column.sortable ? 'pointer' : 'default' }}
                className="user-select-none"
              >
                <div className="d-flex align-items-center">
                  {translate(column.translationKey, language)}
                  {renderSortIcon(column.field, column.sortable)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {players.map((player) => (
            <tr
              key={`${player.playerid}-${player.skaterfullname}-${
                player.mainid || ''
              }`}
              className={
                multiSelectMode && isPlayerSelectedInMultiMode?.(player)
                  ? 'table-success border border-success' 
                  : selectedPlayer === player.playerid 
                    ? 'table-primary' 
                    : ''
              }
              style={
                multiSelectMode && isPlayerSelectedInMultiMode?.(player)
                  ? { 
                      backgroundColor: '#d1e7dd', 
                      fontWeight: 'bold',
                      boxShadow: '0 0 10px rgba(25, 135, 84, 0.3)',
                      animation: 'pulse 2s infinite'
                    }
                  : {}
              }
            >
              {!hideActions && (
                <td>
                  {/* Mobile: keep inline buttons. Tablet/Desktop: show a dropdown to save space. */}
                  <div className="myteams-actions-inline d-flex gap-2">
                    {activeSection !== 'INJURED' && (
                      <>
                        <div
                          className={`player-action ${activeCooldowns.has(player.playerid) ? 'player-disabled' : ''}`}
                          onMouseEnter={(e) => handleMouseEnter(e, player.playerid, 'release')}
                          onMouseLeave={handleMouseLeave}
                        >
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={(e) => handleReleaseClick(player, e)}
                            disabled={activeCooldowns.has(player.playerid)}
                          >
                            {translate('release', language)}
                          </button>
                          {activeCooldowns.has(player.playerid) && (
                            <>
                              <div className="cooldown-overlay">
                                {Math.ceil(getRemainingTime(player.playerid) / 1000)}s
                              </div>
                              <div
                                className="cooldown-progress"
                                style={{
                                  width: `${(getRemainingTime(player.playerid) / 5000) * 100}%`,
                                }}
                              ></div>
                            </>
                          )}
                        </div>

                        {!limitedActions && (<>
                        <div
                          className={`player-action ${activeCooldowns.has(player.playerid) ? 'player-disabled' : ''}`}
                          onMouseEnter={(e) => handleMouseEnter(e, player.playerid, 'toggle')}
                          onMouseLeave={handleMouseLeave}
                        >
                          <button
                            className={`btn btn-sm ${player.isnhl ? 'btn-warning' : 'btn-success'}`}
                            onClick={(e) => handleToggleReserveClick(player, e)}
                            disabled={activeCooldowns.has(player.playerid)}
                          >
                            {player.isnhl ? translate('demote', language) : translate('promote', language)}
                          </button>
                          {activeCooldowns.has(player.playerid) && (
                            <>
                              <div className="cooldown-overlay">
                                {Math.ceil(getRemainingTime(player.playerid) / 1000)}s
                              </div>
                              <div
                                className="cooldown-progress"
                                style={{
                                  width: `${(getRemainingTime(player.playerid) / 5000) * 100}%`,
                                }}
                              ></div>
                            </>
                          )}
                        </div>

                        <div className={`player-action ${activeCooldowns.has(player.playerid) ? 'player-disabled' : ''}`}>
                          <button
                            className={`btn btn-sm ${
                              multiSelectMode && isPlayerSelectedInMultiMode?.(player)
                                ? 'btn-warning active'
                                : multiSelectMode
                                  ? 'btn-outline-warning'
                                  : selectedPlayer === player.playerid
                                    ? 'btn-outline-primary active'
                                    : 'btn-outline-primary'
                            }`}
                            onClick={() => handleSwapSelectClick(player)}
                            disabled={activeCooldowns.has(player.playerid)}
                            title={
                              multiSelectMode
                                ? (language === 'fr' ? 'Sélectionner pour permutation 4 joueurs' : 'Select for 4-player swap')
                                : (language === 'fr' ? 'Sélectionner pour permutation' : 'Select for swapping')
                            }
                          >
                            <RefreshCw size={16} />
                          </button>
                        </div>
                        </>) /* end !isBpmSession */}

                        <div className="player-action">
                          <button
                            className="btn btn-sm btn-outline-info"
                            onClick={(e) => handleViewDetailsClick(player, e)}
                            title={language === 'fr' ? 'Voir les détails du joueur' : 'View player details'}
                          >
                            <Eye size={16} />
                          </button>
                        </div>

                        {/* Boutons Trading Bloc */}
                        {!limitedActions && onToggleTradingBloc && (activeSection === 'MAIN' || activeSection === 'RESERVES') && (
                          <>
                            <div className={`player-action ${activeCooldowns.has(player.playerid) ? 'player-disabled' : ''}`}>
                              <button
                                className={`btn btn-sm ${player.trading_bloc === true ? 'btn-success' : 'btn-outline-success'}`}
                                onClick={(e) => handleToggleTradingBlocClick(player, !player.trading_bloc, e)}
                                disabled={activeCooldowns.has(player.playerid)}
                                title={
                                  language === 'fr'
                                    ? player.trading_bloc === true
                                      ? 'Retirer du Trading Bloc'
                                      : 'Ajouter au Trading Bloc'
                                    : player.trading_bloc === true
                                      ? 'Remove from Trading Bloc'
                                      : 'Add to Trading Bloc'
                                }
                              >
                                <Star size={14} />
                              </button>
                              {activeCooldowns.has(player.playerid) && (
                                <>
                                  <div className="cooldown-overlay">
                                    {Math.ceil(getRemainingTime(player.playerid) / 1000)}s
                                  </div>
                                  <div
                                    className="cooldown-progress"
                                    style={{
                                      width: `${(getRemainingTime(player.playerid) / 5000) * 100}%`,
                                    }}
                                  ></div>
                                </>
                              )}
                            </div>

                            <div className={`player-action ${activeCooldowns.has(player.playerid) ? 'player-disabled' : ''}`}>
                              <button
                                className={`btn btn-sm ${player.is_intouchable === true ? 'btn-danger' : 'btn-outline-warning'}`}
                                onClick={(e) => handleToggleIntouchableClick(player, !player.is_intouchable, e)}
                                disabled={activeCooldowns.has(player.playerid)}
                                title={
                                  language === 'fr'
                                    ? player.is_intouchable === true
                                      ? 'Retirer des Intouchables'
                                      : 'Marquer comme Intouchable'
                                    : player.is_intouchable === true
                                      ? 'Remove from Untouchables'
                                      : 'Mark as Untouchable'
                                }
                              >
                                <Shield size={14} />
                              </button>
                              {activeCooldowns.has(player.playerid) && (
                                <>
                                  <div className="cooldown-overlay">
                                    {Math.ceil(getRemainingTime(player.playerid) / 1000)}s
                                  </div>
                                  <div
                                    className="cooldown-progress"
                                    style={{
                                      width: `${(getRemainingTime(player.playerid) / 5000) * 100}%`,
                                    }}
                                  ></div>
                                </>
                              )}
                            </div>
                          </>
                        )}

                        {!limitedActions && activeSection === 'MAIN' && onMoveToScratch && (
                          <div
                            className={`player-action ${activeCooldowns.has(player.playerid) ? 'player-disabled' : ''}`}
                            onMouseEnter={(e) => handleMouseEnter(e, player.playerid, 'scratch')}
                            onMouseLeave={handleMouseLeave}
                          >
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={(e) => handleMoveToScratchClick(player, e)}
                              disabled={activeCooldowns.has(player.playerid)}
                              title={language === 'fr' ? 'Envoyer vers Scratch' : 'Send to Scratch'}
                            >
                              S
                            </button>
                            {activeCooldowns.has(player.playerid) && (
                              <>
                                <div className="cooldown-overlay">
                                  {Math.ceil(getRemainingTime(player.playerid) / 1000)}s
                                </div>
                                <div
                                  className="cooldown-progress"
                                  style={{
                                    width: `${(getRemainingTime(player.playerid) / 5000) * 100}%`,
                                  }}
                                ></div>
                              </>
                            )}
                          </div>
                        )}

                        {!limitedActions && activeSection === 'SCRATCH' && onRemoveFromScratch && (
                          <div
                            className={`player-action ${activeCooldowns.has(player.playerid) ? 'player-disabled' : ''}`}
                            onMouseEnter={(e) => handleMouseEnter(e, player.playerid, 'unscratch')}
                            onMouseLeave={handleMouseLeave}
                          >
                            <button
                              className="btn btn-sm btn-success"
                              onClick={(e) => handleRemoveFromScratchClick(player, e)}
                              disabled={activeCooldowns.has(player.playerid)}
                              title={language === 'fr' ? 'Retourner au club principal' : 'Return to main club'}
                            >
                              ↩
                            </button>
                            {activeCooldowns.has(player.playerid) && (
                              <>
                                <div className="cooldown-overlay">
                                  {Math.ceil(getRemainingTime(player.playerid) / 1000)}s
                                </div>
                                <div
                                  className="cooldown-progress"
                                  style={{
                                    width: `${(getRemainingTime(player.playerid) / 5000) * 100}%`,
                                  }}
                                ></div>
                              </>
                            )}
                          </div>
                        )}

                        {isPlayerInjured(player.playerid) && onSendToInjuredList && (
                          <div
                            className={`player-action ${activeCooldowns.has(player.playerid) ? 'player-disabled' : ''}`}
                            onMouseEnter={(e) => handleMouseEnter(e, player.playerid, 'injure')}
                            onMouseLeave={handleMouseLeave}
                          >
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={(e) => handleSendToInjuredListClick(player, e)}
                              disabled={activeCooldowns.has(player.playerid)}
                              title="Placer sur LTIR"
                            >
                              <Bandage size={16} />
                            </button>
                            {activeCooldowns.has(player.playerid) && (
                              <>
                                <div className="cooldown-overlay">
                                  {Math.ceil(getRemainingTime(player.playerid) / 1000)}s
                                </div>
                                <div
                                  className="cooldown-progress"
                                  style={{
                                    width: `${(getRemainingTime(player.playerid) / 5000) * 100}%`,
                                  }}
                                ></div>
                              </>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {activeSection === 'INJURED' && onReactivateFromInjured && (
                      <>
                        <div
                          className={`player-action ${activeCooldowns.has(player.playerid) ? 'player-disabled' : ''}`}
                          onMouseEnter={(e) => handleMouseEnter(e, player.playerid, 'release')}
                          onMouseLeave={handleMouseLeave}
                        >
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={(e) => handleReleaseClick(player, e)}
                            disabled={activeCooldowns.has(player.playerid)}
                            title={language === 'fr' ? 'Libérer' : 'Release'}
                          >
                            {translate('release', language)}
                          </button>
                          {activeCooldowns.has(player.playerid) && (
                            <>
                              <div className="cooldown-overlay">
                                {Math.ceil(getRemainingTime(player.playerid) / 1000)}s
                              </div>
                              <div
                                className="cooldown-progress"
                                style={{
                                  width: `${(getRemainingTime(player.playerid) / 5000) * 100}%`,
                                }}
                              ></div>
                            </>
                          )}
                        </div>

                      <div
                        className={`player-action ${activeCooldowns.has(player.playerid) ? 'player-disabled' : ''}`}
                        onMouseEnter={(e) => handleMouseEnter(e, player.playerid, 'reactivate')}
                        onMouseLeave={handleMouseLeave}
                      >
                        <button
                          className="btn btn-sm btn-success"
                          title="Retirer de LTIR"
                          onClick={(e) => handleReactivateFromInjuredClick(player, e)}
                          disabled={activeCooldowns.has(player.playerid)}
                        >
                          <PlusCircle size={16} className="me-1" />
                          Réactiver
                        </button>
                        {activeCooldowns.has(player.playerid) && (
                          <>
                            <div className="cooldown-overlay">
                              {Math.ceil(getRemainingTime(player.playerid) / 1000)}s
                            </div>
                            <div
                              className="cooldown-progress"
                              style={{
                                width: `${(getRemainingTime(player.playerid) / 5000) * 100}%`,
                              }}
                            ></div>
                          </>
                        )}
                      </div>
                      </>
                    )}

                    {/* Watchlist Button */}
                    {!limitedActions && <div className="player-action">
                      {isInWatchlist(player.playerid) ? (
                        <button
                          className="btn btn-sm btn-outline-warning"
                          onClick={(e) => handleRemoveFromWatchlistClick(player, e)}
                          disabled={watchlistActionInProgress}
                          title={language === 'fr' ? 'Retirer de la surveillance' : 'Remove from watchlist'}
                        >
                          {watchlistActionInProgress ? (
                            <span className="spinner-border spinner-border-sm" role="status">
                              <span className="visually-hidden">
                                {language === 'fr' ? 'Chargement...' : 'Loading...'}
                              </span>
                            </span>
                          ) : (
                            <EyeOff size={16} />
                          )}
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={(e) => handleAddToWatchlistClick(player, e)}
                          disabled={watchlistActionInProgress}
                          title={language === 'fr' ? 'Surveiller ce joueur' : 'Watch this player'}
                        >
                          {watchlistActionInProgress ? (
                            <span className="spinner-border spinner-border-sm" role="status">
                              <span className="visually-hidden">
                                {language === 'fr' ? 'Chargement...' : 'Loading...'}
                              </span>
                            </span>
                          ) : (
                            <Eye size={16} />
                          )}
                        </button>
                      )}
                    </div>}
                  </div>

                  <div className="myteams-actions-dropdown dropdown">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary dropdown-toggle myteams-actions-toggle"
                      aria-expanded={openActionsForId === player.playerid}
                      aria-label={language === 'fr' ? 'Actions' : 'Actions'}
                      title={language === 'fr' ? 'Actions' : 'Actions'}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        actionsToggleRef.current = e.currentTarget as unknown as HTMLElement;
                        setOpenActionsForId((prev) => (prev === player.playerid ? null : player.playerid));
                      }}
                    >
                      {/* caret is provided by Bootstrap via .dropdown-toggle::after */}
                    </button>
                    {openActionsForId === player.playerid &&
                      createPortal(
                        <div
                          ref={actionsMenuRef}
                          className="dropdown-menu show"
                          style={actionsMenuStyle}
                          role="menu"
                        >
                          {activeSection !== 'INJURED' && (
                            <>
                              <button
                                type="button"
                                className="dropdown-item text-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeActionsMenu();
                                  handleReleaseClick(player, e as unknown as React.MouseEvent);
                                }}
                                disabled={activeCooldowns.has(player.playerid)}
                              >
                                {translate('release', language)}
                                {activeCooldowns.has(player.playerid)
                                  ? ` (${Math.ceil(getRemainingTime(player.playerid) / 1000)}s)`
                                  : ''}
                              </button>

                              {!limitedActions && <button
                                type="button"
                                className="dropdown-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeActionsMenu();
                                  handleToggleReserveClick(player, e as unknown as React.MouseEvent);
                                }}
                                disabled={activeCooldowns.has(player.playerid)}
                              >
                                {player.isnhl ? translate('demote', language) : translate('promote', language)}
                                {activeCooldowns.has(player.playerid)
                                  ? ` (${Math.ceil(getRemainingTime(player.playerid) / 1000)}s)`
                                  : ''}
                              </button>}

                              {!limitedActions && <button
                                type="button"
                                className="dropdown-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeActionsMenu();
                                  handleSwapSelectClick(player);
                                }}
                                disabled={activeCooldowns.has(player.playerid)}
                              >
                                {multiSelectMode
                                  ? (language === 'fr' ? 'Sélectionner (swap 4 joueurs)' : 'Select (4-player swap)')
                                  : (language === 'fr' ? 'Sélectionner pour permutation' : 'Select for swap')}
                              </button>}

                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeActionsMenu();
                                  handleViewDetailsClick(player, e as unknown as React.MouseEvent);
                                }}
                              >
                                {language === 'fr' ? 'Détails' : 'Details'}
                              </button>

                              {!limitedActions && onToggleTradingBloc && (activeSection === 'MAIN' || activeSection === 'RESERVES') && (
                                <>
                                  <button
                                    type="button"
                                    className="dropdown-item"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      closeActionsMenu();
                                      handleToggleTradingBlocClick(player, !player.trading_bloc, e as unknown as React.MouseEvent);
                                    }}
                                    disabled={activeCooldowns.has(player.playerid)}
                                  >
                                    {language === 'fr'
                                      ? player.trading_bloc === true
                                        ? 'Retirer du Trading Bloc'
                                        : 'Ajouter au Trading Bloc'
                                      : player.trading_bloc === true
                                        ? 'Remove from Trading Bloc'
                                        : 'Add to Trading Bloc'}
                                    {activeCooldowns.has(player.playerid)
                                      ? ` (${Math.ceil(getRemainingTime(player.playerid) / 1000)}s)`
                                      : ''}
                                  </button>

                                  <button
                                    type="button"
                                    className="dropdown-item"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      closeActionsMenu();
                                      handleToggleIntouchableClick(player, !player.is_intouchable, e as unknown as React.MouseEvent);
                                    }}
                                    disabled={activeCooldowns.has(player.playerid)}
                                  >
                                    {language === 'fr'
                                      ? player.is_intouchable === true
                                        ? 'Retirer des Intouchables'
                                        : 'Marquer comme Intouchable'
                                      : player.is_intouchable === true
                                        ? 'Remove from Untouchables'
                                        : 'Mark as Untouchable'}
                                    {activeCooldowns.has(player.playerid)
                                      ? ` (${Math.ceil(getRemainingTime(player.playerid) / 1000)}s)`
                                      : ''}
                                  </button>
                                </>
                              )}

                              {!limitedActions && activeSection === 'MAIN' && onMoveToScratch && (
                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    closeActionsMenu();
                                    handleMoveToScratchClick(player, e as unknown as React.MouseEvent);
                                  }}
                                  disabled={activeCooldowns.has(player.playerid)}
                                >
                                  {language === 'fr' ? 'Envoyer vers Scratch' : 'Send to Scratch'}
                                  {activeCooldowns.has(player.playerid)
                                    ? ` (${Math.ceil(getRemainingTime(player.playerid) / 1000)}s)`
                                    : ''}
                                </button>
                              )}

                              {!limitedActions && activeSection === 'SCRATCH' && onRemoveFromScratch && (
                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    closeActionsMenu();
                                    handleRemoveFromScratchClick(player, e as unknown as React.MouseEvent);
                                  }}
                                  disabled={activeCooldowns.has(player.playerid)}
                                >
                                  {language === 'fr' ? 'Retourner au club principal' : 'Return to main club'}
                                  {activeCooldowns.has(player.playerid)
                                    ? ` (${Math.ceil(getRemainingTime(player.playerid) / 1000)}s)`
                                    : ''}
                                </button>
                              )}

                              {isPlayerInjured(player.playerid) && onSendToInjuredList && (
                                <button
                                  type="button"
                                  className="dropdown-item text-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    closeActionsMenu();
                                    handleSendToInjuredListClick(player, e as unknown as React.MouseEvent);
                                  }}
                                  disabled={activeCooldowns.has(player.playerid)}
                                >
                                  {language === 'fr' ? 'Placer sur LTIR' : 'Place on LTIR'}
                                  {activeCooldowns.has(player.playerid)
                                    ? ` (${Math.ceil(getRemainingTime(player.playerid) / 1000)}s)`
                                    : ''}
                                </button>
                              )}
                            </>
                          )}

                          {activeSection === 'INJURED' && onReactivateFromInjured && (
                            <>
                              <button
                                type="button"
                                className="dropdown-item text-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeActionsMenu();
                                  handleReleaseClick(player, e as unknown as React.MouseEvent);
                                }}
                                disabled={activeCooldowns.has(player.playerid)}
                              >
                                {translate('release', language)}
                                {activeCooldowns.has(player.playerid)
                                  ? ` (${Math.ceil(getRemainingTime(player.playerid) / 1000)}s)`
                                  : ''}
                              </button>

                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeActionsMenu();
                                  handleReactivateFromInjuredClick(player, e as unknown as React.MouseEvent);
                                }}
                                disabled={activeCooldowns.has(player.playerid)}
                              >
                                {language === 'fr' ? 'Réactiver' : 'Reactivate'}
                                {activeCooldowns.has(player.playerid)
                                  ? ` (${Math.ceil(getRemainingTime(player.playerid) / 1000)}s)`
                                  : ''}
                              </button>
                            </>
                          )}

                          {!limitedActions && <>
                          <hr className="dropdown-divider" />

                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              closeActionsMenu();
                              if (isInWatchlist(player.playerid)) {
                                handleRemoveFromWatchlistClick(player, e as unknown as React.MouseEvent);
                              } else {
                                handleAddToWatchlistClick(player, e as unknown as React.MouseEvent);
                              }
                            }}
                            disabled={watchlistActionInProgress}
                          >
                            {language === 'fr'
                              ? isInWatchlist(player.playerid)
                                ? 'Retirer de la surveillance'
                                : 'Surveiller ce joueur'
                              : isInWatchlist(player.playerid)
                                ? 'Remove from watchlist'
                                : 'Watch this player'}
                          </button>
                          </>}
                        </div>,
                        document.body
                      )}
                  </div>
                </td>
              )}
              <td className="position-relative">
                <img 
                  src={getPlayerImageUrl(player)} 
                  alt={player.skaterfullname}
                  style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', cursor: 'pointer' }}
                  className="border"
                  onError={() => handleImageError(player.playerid)}
                  onClick={() => onViewDetails?.(player)}
                />
                {isPlayerInjured(player.playerid) && (
                  <span 
                    className="position-absolute" 
                    style={{ top: '-5px', right: '-5px', cursor: 'pointer' }}
                    onClick={() => handleShowInjuryModal(player)}
                  >
                    <Bandage size={16} className="text-danger" />
                  </span>
                )}
                {activeSection === 'INJURED' && isPlayerReturnedFromInjury(player.playerid) && (
                  <span 
                    className="position-absolute" 
                    style={{ top: '-5px', right: '-5px', cursor: 'pointer' }}
                    onClick={() => handleShowInjuryModal(player)}
                  >
                    <Clock size={16} className="text-warning" />
                  </span>
                )}
              </td>
              {visibleColumns.map((column) => (
                <td
                  key={`${player.playerid}-${column.field}-${column.translationKey}`}
                  data-field={String(column.field)}
                >
                  <div className="d-flex align-items-center">
                    {column.field === 'skaterfullname' ? (
                      <div className="d-flex align-items-center gap-2">
                        {(() => {
                          const fullName = String(getCellValue(player, column) ?? '');
                          const shortName = formatShortName(fullName);
                          return (
                            <>
                              <span className="myteams-player-name-full" style={{ cursor: 'pointer' }} onClick={() => onViewDetails?.(player)}>{fullName}</span>
                              <span className="myteams-player-name-short" style={{ cursor: 'pointer' }} onClick={() => onViewDetails?.(player)}>{shortName}</span>
                            </>
                          );
                        })()}
                        
                        {/* Indicateur Trading Bloc */}
                        {player.trading_bloc === true && (
                          <span 
                            className="badge bg-success d-flex align-items-center gap-1"
                            title="Dans le Trading Bloc (Disponible)"
                            style={{ fontSize: '0.7rem' }}
                          >
                            <Star size={12} /> <span className="myteams-badge-text">Trading</span>
                          </span>
                        )}
                        
                        {/* Indicateur Intouchable */}
                        {player.is_intouchable === true && (
                          <span 
                            className="badge bg-danger d-flex align-items-center gap-1"
                            title="Joueur Intouchable"
                            style={{ fontSize: '0.7rem' }}
                          >
                            <Shield size={12} /> <span className="myteams-badge-text">Intouchable</span>
                          </span>
                        )}
                        
                        {isPlayerInjured(player.playerid) && (
                          <Bandage 
                            size={16} 
                            className="text-danger ms-2"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleShowInjuryModal(player)}
                          />
                        )}
                        
                        {activeSection === 'INJURED' && isPlayerReturnedFromInjury(player.playerid) && (
                          <Clock
                            size={16}
                            className="text-warning ms-2"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleShowInjuryModal(player)}
                          />
                        )}
                      </div>
                    ) : (
                      getCellValue(player, column)
                    )}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      
      {tooltipInfo && (
        <div
          className="cooldown-tooltip"
          style={{
            top: `${tooltipInfo.y}px`,
            left: `${tooltipInfo.x}px`,
          }}
        >
          {language === 'en' ? 'Cooldown active:' : 'Délai actif:'} {Math.ceil(getRemainingTime(tooltipInfo.playerId) / 1000)}s
        </div>
      )}

      <InjuryDescriptionModal
        show={showInjuryModal}
        onClose={() => setShowInjuryModal(false)}
        playerName={selectedInjury?.playerName || ''}
        description={selectedInjury?.description || ''}
        language={language}
        daysRemaining={selectedInjury?.daysRemaining}
        isReturned={selectedInjury?.isReturned}
      />
    </div>
  );
}
